import { useState, useEffect, useRef } from "react";
import { Search, Package, FileText, Users, Building2, MapPin, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { getStockItems } from "@/services/stockDataService";
import { getInitializedLocations } from "@/data/initializeLocationStock";
import { mockWarehouses } from "@/data/mockWarehousesLocations";
import { useNavigation } from "@/components/layout/app-layout";

interface SearchResult {
  id: string;
  type: "page" | "stock-item" | "supplier" | "customer" | "location" | "warehouse";
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  action: () => void;
}

// Define searchable pages
const searchablePages = [
  { id: "dashboard", title: "Home", keywords: ["dashboard", "overview"] },
  { id: "stock-items", title: "Stock Items", keywords: ["inventory", "products", "items"] },
  { id: "warehouses", title: "Locations", keywords: ["warehouse", "location", "storage"] },
  { id: "stock-movements", title: "Stock Movements", keywords: ["movement", "transfer", "adjust", "history", "transactions"] },
  { id: "stock-takes", title: "Stock Takes", keywords: ["count", "audit", "stocktake"] },
  { id: "tracking", title: "Lot/Batch/Serial Tracking", keywords: ["lot", "batch", "serial", "tracking"] },
  { id: "purchase-orders", title: "Purchase Orders", keywords: ["po", "purchase", "order"] },
  { id: "receive-goods", title: "Receive Goods", keywords: ["receive", "grn", "goods receipt"] },
  { id: "suppliers", title: "Suppliers", keywords: ["vendor", "supplier"] },
  { id: "sales-orders", title: "Sales Orders", keywords: ["so", "sales", "order"] },
  { id: "pick-pack", title: "Pick & Pack", keywords: ["pick", "pack", "fulfillment"] },
  { id: "shipping", title: "Shipping", keywords: ["ship", "dispatch", "delivery"] },
  { id: "customers", title: "Customers", keywords: ["client", "customer"] },
  { id: "projects-cost-centers", title: "Projects & Cost Centers", keywords: ["project", "cost center"] },
  { id: "boms", title: "Bills of Material", keywords: ["bom", "bill of material", "recipe"] },
  { id: "work-orders", title: "Work Orders", keywords: ["wo", "work order", "production"] },
  { id: "production-schedule", title: "Production Schedule", keywords: ["schedule", "planning"] },
  { id: "routing", title: "Routings & Work Centers", keywords: ["routing", "work center"] },
  { id: "ai-forecasting", title: "Demand Forecasting", keywords: ["forecast", "demand", "ai", "prediction"] },
  { id: "abc-classification", title: "ABC Classification", keywords: ["abc", "classification", "analysis"] },
  { id: "reorder", title: "Reorder Recommendations", keywords: ["reorder", "replenishment"] },
  { id: "price-matrix", title: "Price Matrix", keywords: ["price", "pricing"] },
  { id: "margin-analysis", title: "Margin Analysis", keywords: ["margin", "profit"] },
  { id: "cost-analysis", title: "Cost Analysis", keywords: ["cost", "costing"] },
  { id: "stock-options", title: "System Configuration", keywords: ["settings", "config", "options"] },
  { id: "integration-sync", title: "Integration", keywords: ["integration", "sync", "api"] },
  { id: "company-settings", title: "Company Settings", keywords: ["company", "organisation"] },
  { id: "user-management", title: "User Management", keywords: ["user", "permissions", "roles"] },
  { id: "custom-fields", title: "Custom Fields", keywords: ["custom", "fields", "attributes"] },
];

export function GlobalSearch() {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const { navigateTo } = useNavigation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Search function
  useEffect(() => {
    // Only search if query has at least 3 characters
    if (!searchQuery.trim() || searchQuery.trim().length < 3) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    const query = searchQuery.toLowerCase();
    const searchResults: SearchResult[] = [];

    // Search pages
    searchablePages.forEach((page) => {
      const titleMatch = page.title.toLowerCase().includes(query);
      const keywordMatch = page.keywords.some((keyword) => keyword.includes(query));

      if (titleMatch || keywordMatch) {
        searchResults.push({
          id: page.id,
          type: "page",
          title: page.title,
          subtitle: "Page",
          icon: <FileText className="h-4 w-4" />,
          action: () => {
            navigateTo(page.id);
            setSearchQuery("");
            setIsOpen(false);
          },
        });
      }
    });

    // Search stock items
    try {
      const stockItems = getStockItems();
      stockItems
        .filter((item) => {
          const codeMatch = item.code.toLowerCase().includes(query);
          const descMatch = item.description.toLowerCase().includes(query);
          const barcodeMatch = item.barcode?.toLowerCase().includes(query);
          return codeMatch || descMatch || barcodeMatch;
        })
        .slice(0, 5) // Limit to 5 results
        .forEach((item) => {
          searchResults.push({
            id: item.code,
            type: "stock-item",
            title: item.description,
            subtitle: `Code: ${item.code}`,
            icon: <Package className="h-4 w-4" />,
            action: () => {
              navigateTo("stock-item-detail", { itemCode: item.code });
              setSearchQuery("");
              setIsOpen(false);
            },
          });
        });
    } catch (error) {
      console.error("Error searching stock items:", error);
    }

    // Search locations
    try {
      const locations = getInitializedLocations();
      locations
        .filter((location) => {
          const locationCodeMatch = location.locationCode.toLowerCase().includes(query);
          const warehouseMatch = location.warehouseCode.toLowerCase().includes(query);
          const typeMatch = location.locationType.toLowerCase().includes(query);
          const aisleMatch = location.aisle?.toLowerCase().includes(query);
          const rackMatch = location.rack?.toLowerCase().includes(query);
          return locationCodeMatch || warehouseMatch || typeMatch || aisleMatch || rackMatch;
        })
        .slice(0, 5) // Limit to 5 results
        .forEach((location) => {
          searchResults.push({
            id: location.locationCode,
            type: "location",
            title: location.locationCode,
            subtitle: `${location.warehouseCode} - ${location.locationType}`,
            icon: <MapPin className="h-4 w-4" />,
            action: () => {
              navigateTo("warehouses", { selectedLocation: location.locationCode });
              setSearchQuery("");
              setIsOpen(false);
            },
          });
        });
    } catch (error) {
      console.error("Error searching locations:", error);
    }

    // Search warehouses
    try {
      mockWarehouses
        .filter((warehouse) => {
          const nameMatch = warehouse.name.toLowerCase().includes(query);
          const codeMatch = warehouse.code.toLowerCase().includes(query);
          const addressMatch = warehouse.address.toLowerCase().includes(query);
          const typeMatch = warehouse.type.toLowerCase().includes(query);
          return nameMatch || codeMatch || addressMatch || typeMatch;
        })
        .slice(0, 5) // Limit to 5 results
        .forEach((warehouse) => {
          searchResults.push({
            id: warehouse.code,
            type: "warehouse",
            title: warehouse.name,
            subtitle: `${warehouse.code} - ${warehouse.type}`,
            icon: <Building2 className="h-4 w-4" />,
            action: () => {
              navigateTo("warehouses", { selectedWarehouse: warehouse.code });
              setSearchQuery("");
              setIsOpen(false);
            },
          });
        });
    } catch (error) {
      console.error("Error searching warehouses:", error);
    }

    setResults(searchResults);
    setIsOpen(searchResults.length > 0);
    setSelectedIndex(0);
  }, [searchQuery, navigateTo]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || results.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % results.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + results.length) % results.length);
        break;
      case "Enter":
        e.preventDefault();
        if (results[selectedIndex]) {
          results[selectedIndex].action();
        }
        break;
      case "Escape":
        e.preventDefault();
        setIsOpen(false);
        setSearchQuery("");
        inputRef.current?.blur();
        break;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "page":
        return <FileText className="h-4 w-4 text-muted-foreground" />;
      case "stock-item":
        return <Package className="h-4 w-4 text-blue-500" />;
      case "supplier":
        return <Building2 className="h-4 w-4 text-green-500" />;
      case "customer":
        return <Users className="h-4 w-4 text-purple-500" />;
      case "location":
        return <MapPin className="h-4 w-4 text-orange-500" />;
      case "warehouse":
        return <Building2 className="h-4 w-4 text-green-500" />;
      default:
        return <FileText className="h-4 w-4 text-muted-foreground" />;
    }
  };

  return (
    <div ref={searchRef} className="relative w-full max-w-lg">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Search Stock Evo..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (searchQuery.trim().length >= 3 && results.length > 0) {
              setIsOpen(true);
            }
          }}
          className="pl-9 pr-4 h-9"
        />
      </div>

      {/* Results Dropdown */}
      {isOpen && results.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-popover border rounded-md shadow-lg z-[9999] max-h-[400px] overflow-y-auto">
          <div className="p-2">
            {results.map((result, index) => (
              <button
                key={`${result.type}-${result.id}`}
                onClick={result.action}
                onMouseEnter={() => setSelectedIndex(index)}
                className={cn(
                  "w-full flex items-center gap-3 px-3 py-2 rounded-sm text-left transition-colors",
                  index === selectedIndex
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent/50"
                )}
              >
                <div className="flex-shrink-0">{getTypeIcon(result.type)}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{result.title}</div>
                  {result.subtitle && (
                    <div className="text-xs text-muted-foreground truncate">
                      {result.subtitle}
                    </div>
                  )}
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            ))}
          </div>

          {/* Footer with keyboard hints */}
          <div className="border-t px-3 py-2 text-xs text-muted-foreground bg-muted/50">
            <div className="flex items-center justify-between">
              <span>Use ↑↓ to navigate</span>
              <span>↵ to select</span>
              <span>ESC to close</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
